// UI Components
import {
  Badge,
  Divider,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Flex,
  ListItem,
  Text,
  VisuallyHidden,
} from '@/components/ui';
import React, { useMemo } from 'react';
import {
  IconChevronDown,
  IconColumns,
  IconDatabase,
  IconTable,
  IconWand,
  IconX,
} from '@tabler/icons-react';

// Types
import { ColumnMapping, StudyForm } from './types';

interface ColumnFieldSelectorProps {
  columnName: string;
  form: StudyForm;
  fileMappings: ColumnMapping[];
  fileId: string;
  onMappingChange: (
    fileId: string,
    columnName: string,
    mappingType: 'formField' | 'metadata' | 'ignored',
    targetField?: string
  ) => void;
  showAsDropdownContent?: boolean;
  triggerLabel?: string;
  triggerIcon?: React.ReactNode;
  triggerVariant?: 'surface' | 'dashed' | 'soft' | 'solid';
  triggerColor?: 'success' | 'info' | 'default' | 'warning' | 'danger';
}

export const ColumnFieldSelector: React.FC<ColumnFieldSelectorProps> = ({
  columnName,
  form,
  fileMappings,
  fileId,
  onMappingChange,
  showAsDropdownContent = false,
  triggerLabel,
  triggerIcon,
  triggerVariant,
  triggerColor,
}) => {
  // Get current mapping for this column
  const currentMapping = useMemo(() => {
    return fileMappings.find(
      m => m.fileId === fileId && m.columnName === columnName
    );
  }, [fileMappings, fileId, columnName]);

  // Note: validationErrors not available in legacy ColumnMapping type
  const validationErrors: string[] = []; // TODO: Add validation errors support

  // Get all form fields with their availability status
  const formFieldsWithStatus = useMemo(() => {
    const mappedFormFields = new Set<string>();

    // Find all form field mappings
    fileMappings.forEach(cm => {
      if (cm.mappingType === 'formField' && cm.targetField) {
        mappedFormFields.add(cm.targetField);
      }
    });

    return (
      form.fields?.map(field => ({
        ...field,
        isAvailable: !mappedFormFields.has(field.dataPoint),
        mappedByColumn: Array.from(mappedFormFields).find(
          target => target === field.dataPoint
        )
          ? fileMappings.find(
              cm =>
                cm.mappingType === 'formField' &&
                cm.targetField === field.dataPoint
            )?.columnName
          : undefined,
      })) || []
    );
  }, [fileMappings, form.fields]);

  // Get all metadata types with their availability status
  const metadataTypesWithStatus = useMemo(() => {
    const mappedMetadata = new Set<string>();

    fileMappings.forEach(cm => {
      if (cm.mappingType === 'metadata') {
        mappedMetadata.add(cm.metadataType?.type || '');
      }
    });

    const allMetadataTypes: {
      type: string;
      label: string;
      required: boolean;
    }[] = [
      {
        type: 'externalSubjectId',
        label: 'External Subject ID',
        required: true,
      },
      { type: 'subjectNumber', label: 'Subject Number', required: true },
      { type: 'siteNumber', label: 'Site Number', required: true },
      { type: 'site', label: 'Site ID', required: false },
      { type: 'siteName', label: 'Site Name', required: false },
      { type: 'formId', label: 'Form ID', required: false },
      { type: 'mainFormId', label: 'Main Form ID', required: false },
      { type: 'subjectData', label: 'Subject Data', required: false },
      { type: 'deletedForm', label: 'Deleted Form', required: false },
      { type: 'formIndex', label: 'Form Index', required: false },
      { type: 'trash', label: 'Trash', required: false },
    ];

    return allMetadataTypes.map(meta => ({
      ...meta,
      isAvailable: !mappedMetadata.has(meta.type),
      mappedByColumn: mappedMetadata.has(meta.type)
        ? fileMappings.find(
            cm =>
              cm.mappingType === 'metadata' &&
              cm.metadataType?.type === meta.type
          )?.columnName
        : undefined,
    }));
  }, [fileMappings]);

  const handleMapToFormField = (targetField: string) => {
    onMappingChange(fileId, columnName, 'formField', targetField);
  };

  const handleMapToMetadata = (metadataType: string) => {
    onMappingChange(fileId, columnName, 'metadata', metadataType);
  };

  const handleIgnoreColumn = () => {
    onMappingChange(fileId, columnName, 'ignored');
  };

  const handleAutoMap = () => {
    // Try to find exact match
    const exactMatch = form.fields?.find(
      field => field.dataPoint.toLowerCase() === columnName.toLowerCase()
    );

    if (
      exactMatch &&
      !formFieldsWithStatus.some(
        f => f.isAvailable && f.dataPoint === exactMatch.dataPoint
      )
    ) {
      // Field already mapped, can't auto-map
      return;
    }

    if (exactMatch) {
      handleMapToFormField(exactMatch.dataPoint);
    }
  };

  const getStatusColor = ():
    | 'success'
    | 'info'
    | 'warning'
    | 'danger'
    | 'default' => {
    if (validationErrors.length > 0) return 'danger';
    if (currentMapping?.mappingType === 'formField') return 'success';
    if (currentMapping?.mappingType === 'metadata') return 'info';
    if (currentMapping?.mappingType === 'ignored') return 'default';
    return 'warning';
  };

  type SelectorColor = ReturnType<typeof getStatusColor>;

  const toBadgeColor = (
    color: SelectorColor
  ): React.ComponentProps<typeof Badge>['color'] => {
    switch (color) {
      case 'danger':
        return 'error';
      default:
        return color;
    }
  };

  const getStatusVariant = () => {
    // If we have a current mapping, always use surface
    if (currentMapping) {
      return 'surface';
    }

    // If no mapping, use dashed for unmapped columns
    return 'dashed';
  };

  const getStatusIcon = () => {
    if (validationErrors.length > 0) {
      return <IconX color="var(--red-9)" />;
    }
    if (currentMapping?.mappingType === 'formField') {
      return <IconColumns color="var(--green-9)" />;
    }
    if (currentMapping?.mappingType === 'metadata') {
      return <IconDatabase color="var(--blue-9)" />;
    }
    if (currentMapping?.mappingType === 'ignored') {
      return <IconX color="var(--gray-9)" />;
    }
    return undefined;
  };

  const getMappingDisplayText = () => {
    if (validationErrors.length > 0) {
      return 'Validation Error';
    }

    switch (currentMapping?.mappingType) {
      case 'formField':
        return currentMapping.targetField || 'Form Field';
      case 'metadata':
        return (
          currentMapping.metadataType?.label ||
          currentMapping.metadataType?.type ||
          'Metadata'
        );
      case 'ignored':
        return 'Ignored';
      default:
        return 'Select mapping';
    }
  };

  const dropdownContent = (
    <DropdownMenuContent
      variant="soft"
      style={{ minWidth: '300px', maxHeight: '350px', overflow: 'auto' }}
    >
      {/* Ignore option */}
      <DropdownMenuItem asChild>
        <ListItem
          startIcon={<IconX color="var(--red-9)" />}
          primary={'Ignore Column'}
          secondary="Don't import this column"
          onClick={handleIgnoreColumn}
        />
      </DropdownMenuItem>

      {/* Form Fields */}
      {formFieldsWithStatus.length > 0 && (
        <>
          <Divider />
          <DropdownMenuLabel>
            Form Fields (
            {formFieldsWithStatus.filter(f => f.isAvailable).length} available)
          </DropdownMenuLabel>

          {formFieldsWithStatus.map(field => (
            <DropdownMenuItem
              key={field.dataPoint}
              disabled={!field.isAvailable}
              asChild
            >
              <ListItem
                primary={
                  <Flex align="center" gap="2">
                    <IconTable
                      size={14}
                      color={
                        field.isAvailable ? 'var(--purple-9)' : 'var(--gray-9)'
                      }
                    />
                    <Text style={{ opacity: field.isAvailable ? 1 : 0.5 }}>
                      {field.dataPoint}
                    </Text>
                    {field.required && (
                      <Badge size="xsmall" color="red">
                        Required
                      </Badge>
                    )}
                    {!field.isAvailable && field.mappedByColumn && (
                      <Badge size="xsmall" color="info" variant="surface">
                        Mapped by {field.mappedByColumn}
                      </Badge>
                    )}
                  </Flex>
                }
                secondary={
                  field.isAvailable
                    ? field.label
                    : `Already mapped to column "${field.mappedByColumn}"`
                }
                onClick={
                  field.isAvailable
                    ? () => handleMapToFormField(field.dataPoint)
                    : undefined
                }
              />
            </DropdownMenuItem>
          ))}
        </>
      )}

      {/* Metadata Types */}
      {metadataTypesWithStatus.length > 0 && (
        <>
          <Divider />
          <DropdownMenuLabel>
            Metadata Types (
            {metadataTypesWithStatus.filter(m => m.isAvailable).length}{' '}
            available)
          </DropdownMenuLabel>

          {metadataTypesWithStatus.map(meta => (
            <DropdownMenuItem
              key={meta.type}
              disabled={!meta.isAvailable}
              asChild
            >
              <ListItem
                primary={
                  <Flex align="center" gap="2">
                    <IconDatabase
                      size={14}
                      color={
                        meta.isAvailable ? 'var(--blue-9)' : 'var(--gray-9)'
                      }
                    />
                    <Text style={{ opacity: meta.isAvailable ? 1 : 0.5 }}>
                      {meta.label}
                    </Text>
                    {meta.required && (
                      <Badge size="xsmall" color="error">
                        Required
                      </Badge>
                    )}
                    {!meta.isAvailable && meta.mappedByColumn && (
                      <Badge size="xsmall" color="gray" variant="soft">
                        Mapped by {meta.mappedByColumn}
                      </Badge>
                    )}
                  </Flex>
                }
                secondary={
                  meta.isAvailable
                    ? undefined
                    : `Already mapped to column "${meta.mappedByColumn}"`
                }
                onClick={
                  meta.isAvailable
                    ? () => handleMapToMetadata(meta.type)
                    : undefined
                }
              />
            </DropdownMenuItem>
          ))}
        </>
      )}

      {/* Auto-map option */}
      <Divider />
      <DropdownMenuItem asChild>
        <ListItem
          size="3xsmall"
          startIcon={<IconWand size={14} color="var(--blue-9)" />}
          primary={'Try Auto-Map'}
          secondary="Automatically match column to form field"
          onClick={handleAutoMap}
        />
      </DropdownMenuItem>
    </DropdownMenuContent>
  );

  if (showAsDropdownContent) {
    return dropdownContent;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <Badge
          variant={triggerVariant || getStatusVariant()}
          size="xsmall"
          color={toBadgeColor(triggerColor || getStatusColor())}
          startIcon={triggerIcon !== undefined ? triggerIcon : getStatusIcon()}
          endIcon={<IconChevronDown />}
          style={{
            cursor: 'pointer',
          }}
        >
          {triggerLabel !== undefined ? triggerLabel : getMappingDisplayText()}
          <VisuallyHidden>
            {currentMapping?.confidence &&
              currentMapping.confidence < 1.0 &&
              currentMapping.confidence > 0 && (
                <Badge size="xsmall" color="orange" variant="soft">
                  {Math.round(currentMapping.confidence * 100)}%
                </Badge>
              )}
          </VisuallyHidden>
        </Badge>
      </DropdownMenuTrigger>
      {dropdownContent}
    </DropdownMenu>
  );
};

export default ColumnFieldSelector;
